import React, { useCallback, useMemo, useState } from "react";

import { makeStyles } from "tss-react/mui";

import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";

import CloseIcon from "@mui/icons-material/Close";
import PercentIcon from "@mui/icons-material/Percent";

import { regexName } from "../../../../../util/regularExpress";
import { groupTypes } from "../data";
import SmallToolbar from "../../../../Common/SmallToolbar";

const useStyles = makeStyles()((theme) => ({
  Group: {
    padding: "15px",
  },
}));

const CustomPaper = (props) => {
  return (
    <Paper
      {...props}
      sx={{
        "& .MuiAutocomplete-listbox": {
          maxWidth: "100%",
          wordBreak: "break-all !important",
        },
      }}
    />
  );
};

function RuleGroup({ group, index, setGroups, fields: optionFields, groups }) {
  const { classes } = useStyles();

  const { fields, method, threshold = 90 } = useMemo(() => group, [group]);

  const [thresholdError, setThresholdError] = useState("");

  const handleFieldsChange = useCallback(
    (e, selectedObj) => {
      setGroups((prevGroups) => {
        const groups = [...prevGroups];
        groups[index].fields = selectedObj;
        return groups;
      });
    },
    [index, setGroups]
  );

  const handleThresholdChange = useCallback(
    (e) => {
      if (regexName(e.target.value, "numeric")) {
        setGroups((prev) => {
          const groups = [...prev];
          const newVal = Number(e.target.value);
          groups[index].threshold = newVal;
          if (newVal <= 0 || newVal >= 100) setThresholdError("Threshold value should be between 1% to 99%.");
          else setThresholdError("");
          return groups;
        });
      }
    },
    [index, setGroups]
  );

  const handleGroupMethodChange = useCallback(
    (e) => {
      setGroups((prev) => {
        const groups = [...prev];
        groups[index].method = e.target.value;
        return groups;
      });
    },
    [index, setGroups]
  );

  // Filtering-out all the already selected fields in other groups
  const $optionFields = useMemo(() => {
    const fieldsToReturn = optionFields.filter(
      ({ name }) =>
        fields.some(({ name: fname }) => fname === name) ||
        !groups.some((group) => group.fields.find(({ name: fname }) => fname === name))
    );

    return JSON.parse(JSON.stringify([...fieldsToReturn]));
  }, [fields, groups, optionFields]);

  const $nullCheckFieldError = useMemo(() => fields.length >= 2 && method === "isn", [fields.length, method]);

  return (
    <>
      <Paper elevation={2} className={classes.Group}>
        <Box display="flex" sx={{ paddingBottom: "10px" }}>
          <SmallToolbar style={{ flex: 1 }}>{`Group ${index + 1}`}</SmallToolbar>
          <IconButton
            onClick={() =>
              setGroups((prev) => {
                const groups = [...prev];
                groups.splice(index, 1);
                return groups;
              })
            }
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Grid container spacing={2}>
          <Grid size={12}>
            <Autocomplete
              multiple
              fullWidth
              PaperComponent={CustomPaper}
              options={$optionFields}
              value={fields}
              disableCloseOnSelect
              isOptionEqualToValue={(option, value) => {
                return option.id === value.id;
              }}
              getOptionLabel={(option) => option.name}
              onChange={handleFieldsChange}
              renderOption={(props, option, { selected }) => (
                <li {...props} key={option.value}>
                  <Checkbox style={{ marginRight: 8 }} checked={selected} />
                  {option.name}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  variant="outlined"
                  label="Fields"
                  required
                  error={$nullCheckFieldError}
                  helperText={$nullCheckFieldError && "For the IfNull method, multiple fields cannot be selected."}
                />
              )}
              renderTags={(selected) => <Typography noWrap>{selected.map(({ name }) => name).join(",")}</Typography>}
            />
          </Grid>
          <Grid size={8}>
            <FormControl variant="outlined" required fullWidth>
              <InputLabel>Method</InputLabel>
              <Select label="Method" value={method} onChange={handleGroupMethodChange}>
                {groupTypes.map(({ id, label }) => (
                  <MenuItem key={id} value={id}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {method === "jw" && (
            <Grid size={4}>
              <TextField
                InputProps={{
                  inputProps: { min: 1, max: 99 },
                  endAdornment: (
                    <InputAdornment position="end">
                      <PercentIcon />
                    </InputAdornment>
                  ),
                }}
                type="number"
                value={threshold}
                required
                fullWidth
                label="Threshold"
                onChange={handleThresholdChange}
                helperText={thresholdError}
                error={thresholdError !== ""}
              />
            </Grid>
          )}
        </Grid>
      </Paper>
    </>
  );
}

export default RuleGroup;
